import React, { createContext, useContext, useCallback, useRef } from 'react';

interface RefreshContextType {
  registerRefreshHandler: (key: string, handler: () => void) => void;
  unregisterRefreshHandler: (key: string) => void;
  refreshAll: () => void;
}

const RefreshContext = createContext<RefreshContextType | undefined>(undefined);

export const RefreshProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const handlersRef = useRef<Map<string, () => void>>(new Map());

  const registerRefreshHandler = useCallback((key: string, handler: () => void) => {
    handlersRef.current.set(key, handler);
  }, []);

  const unregisterRefreshHandler = useCallback((key: string) => {
    handlersRef.current.delete(key);
  }, []);

  const refreshAll = useCallback(() => {
    handlersRef.current.forEach((handler, key) => {
      try {
        handler();
      } catch (error) {
        // Silently handle refresh errors
      }
    });
  }, []);

  const value = {
    registerRefreshHandler,
    unregisterRefreshHandler,
    refreshAll,
  };

  return (
    <RefreshContext.Provider value={value}>
      {children}
    </RefreshContext.Provider>
  );
};

export const useGlobalRefresh = () => {
  const context = useContext(RefreshContext);
  if (context === undefined) {
    throw new Error('useGlobalRefresh must be used within a RefreshProvider');
  }
  return context;
};

// Hook for components to register their refresh handlers
export const useRegisterRefresh = (key: string, refreshHandler: () => void) => {
  const { registerRefreshHandler, unregisterRefreshHandler } = useGlobalRefresh();

  React.useEffect(() => {
    registerRefreshHandler(key, refreshHandler);
    return () => unregisterRefreshHandler(key);
  }, [key, refreshHandler, registerRefreshHandler, unregisterRefreshHandler]);
};
